import { createRoute, Link } from "@tanstack/react-router";
import { RootRoute } from "../root-route";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { useQuery } from "@tanstack/react-query";
import { getAllKommunStatusAgg } from "@/lib/api";
import { RESTRIKTION_LABEL, RESTRIKTION_CLASSES, formatRelativeTime, type RestriktionTyp } from "@/lib/status";

export const KallorRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/kallor",
  component: Kallor,
  head: () => ({
    meta: [
      { title: "Källor | Bevattningskartan" },
      { name: "description", content: "Datakällor och metod för Bevattningskartan." },
    ],
    links: [
      { rel: "canonical", href: "https://bevattningskartan.se/kallor" },
    ],
  }),
});

interface KommunRow {
  kommunkod: string;
  kommun_namn: string;
  lan: string | null;
  va_namn: string | null;
  typ: RestriktionTyp | null;
  senast_scrapad: string | null;
}

function Kallor() {
  const { data: kommuner = [], isLoading } = useQuery({
    queryKey: ["kommun-status-agg"],
    queryFn: getAllKommunStatusAgg,
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <header className="max-w-2xl">
          <h1 className="font-display text-4xl sm:text-5xl text-foreground">
            Källor i bevakning
          </h1>
          <p className="mt-3 text-muted-foreground">
            Vi övervakar publika sidor hos följande VA-huvudmän. Innehållet tolkas automatiskt — vid osäkerhet visar vi alltid "ingen data" istället för att gissa.
          </p>
        </header>

        <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-5 py-3 font-medium">Kommun</th>
                <th className="px-5 py-3 font-medium">VA-huvudman</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Senast kontrollerad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-muted-foreground">
                    Laddar…
                  </td>
                </tr>
              )}
              {kommuner.map((k) => {
                const typ = k.typ ?? "ingen";
                const cls = RESTRIKTION_CLASSES[typ as keyof typeof RESTRIKTION_CLASSES] || RESTRIKTION_CLASSES.ingen;
                return (
                  <tr key={k.kommunkod} className="hover:bg-muted/30">
                    <td className="px-5 py-4">
                      <Link
                        to="/kommun/$kommunkod"
                        params={{ kommunkod: k.kommunkod }}
                        className="font-medium text-foreground hover:underline"
                      >
                        {k.kommun_namn}
                      </Link>
                      {k.lan && (
                        <div className="text-xs text-muted-foreground">{k.lan}</div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-muted-foreground">
                      {k.va_namn || "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 rounded-full ${cls.bg} px-2.5 py-1 text-xs font-medium ${cls.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${cls.dot}`} />
                        {RESTRIKTION_LABEL[typ as keyof typeof RESTRIKTION_LABEL] || typ}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground">
                      {formatRelativeTime(k.senast_scrapad)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {kommuner.length === 0 && !isLoading && (
          <div className="mt-8 rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">
            Inga kommuner tillgängliga just nu.
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
